import { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { ROUTES } from '@/shared/lib/routes'
import type { ReactNode } from 'react'
import { useSession, useLogin } from '../hooks/useAuth'
import { LoadingSpinner } from '@/shared/components/LoadingSpinner'
import { getSilentCredentials } from '@/shared/lib/credentials'
import { activateLocale, locales, type Locale } from '@/i18n'

type AutoLoginState = 'idle' | 'trying' | 'succeeded' | 'failed'

export function AuthGuard({ children }: { children: ReactNode }) {
  const { data: user, isLoading, isError } = useSession()
  const [autoLogin, setAutoLogin] = useState<AutoLoginState>('idle')
  const login = useLogin()

  useEffect(() => {
    if (!isError || autoLogin !== 'idle') return

    setAutoLogin('trying')

    getSilentCredentials()
      .then((cred) => {
        if (!cred) {
          setAutoLogin('failed')
          return
        }
        login.mutate(
          { email: cred.id, password: cred.password },
          {
            onSuccess: () => setAutoLogin('succeeded'),
            onError: () => setAutoLogin('failed'),
          },
        )
      })
      .catch(() => setAutoLogin('failed'))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- login.mutate is stable (TanStack Query)
  }, [isError, autoLogin])

  const localeSynced = useRef(false)
  useEffect(() => {
    if (!user?.lang || localeSynced.current) return
    const apiLang = user.lang as Locale
    if (apiLang in locales) {
      localeSynced.current = true
      activateLocale(apiLang)
    }
  }, [user])

  if (
    isLoading ||
    autoLogin === 'trying' ||
    (autoLogin === 'succeeded' && !user)
  ) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (user) return <>{children}</>

  return <Navigate to={ROUTES.login} replace />
}
